/**
 * Billing utilities - product/plan mapping and display helpers.
 * @module lib/billing
 */

import { resolvePlanTier } from './planConfig';

/**
 * Returns the display name for a given Stripe product ID.
 * Returns "Free" when the subscription has no plan id, and also when the
 * product id is unrecognized (the resolver falls back to the free tier and
 * captures the mismatch to PostHog).
 * @param productId - Stripe product ID
 */
export function getPlanDisplayName(productId: string | undefined): string {
  const tier = resolvePlanTier(productId);
  if (tier === 'free') return 'Free';
  if (tier === 'pro') return 'Pro';
  if (tier === 'business') return 'Business';
  throw new Error(`Unsupported pricing plan tier: ${tier satisfies never}`);
}

/** Subscription details returned from the billing/subscription endpoint. */
export interface SubscriptionDetails {
  /** Subscription status (active, canceled, past_due, trialing, none, etc.) */
  status: string;
  /** Human-readable plan name */
  plan_name?: string;
  /** Stripe product ID */
  product_id?: string;
  /** Unix timestamp (seconds) for end of current billing period */
  current_period_end?: number;
  /** Whether the subscription will cancel at the end of the current billing period. */
  cancel_at_period_end?: boolean;
  /**
   * Unix timestamp (seconds) when the subscription is scheduled to cancel.
   * This is the actual cancellation date and may differ from
   * current_period_end (e.g. a fixed future date). Independent of
   * cancel_at_period_end: either can drive a pending cancellation.
   */
  cancel_at?: number | null;
  /** Unix timestamp (seconds) when the trial started, or null when no trial. */
  trial_start?: number | null;
  /** Unix timestamp (seconds) when the trial ends, or null when no trial. */
  trial_end?: number | null;
  /** Stripe price ID the subscription is bound to. Used to look up the
   *  post-trial charge amount / interval in the pricing-plans catalogue. */
  price_id?: string;
}
